import { Check, Copy } from "lucide-react";
import * as React from "react";
import { notify } from "../../lib/notify";
import { cn } from "../../lib/utils";
import { Button, ButtonProps } from "./button";

interface CopyButtonProps extends Omit<
  ButtonProps,
  "onClick" | "children" | "onCopy"
> {
  value: string;
  label?: string;
  successMessage?: string;
  showLabel?: boolean;
  expandOnHover?: boolean;
  onCopy?: (value: string) => void;
}

export function CopyButton({
  value,
  label = "Copy",
  successMessage = "Copied to clipboard",
  showLabel = false,
  expandOnHover = false,
  className,
  variant = "ghost",
  size = "sm",
  onCopy,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = React.useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        onCopy?.(value);
        notify.success(successMessage);
        setTimeout(() => setCopied(false), 1500);
      } catch (err) {
        notify.error("Couldn't copy", {
          description: "Your browser blocked clipboard access.",
        });
      }
    },
    [value, successMessage, onCopy]
  );

  if (expandOnHover) {
    return (
      <button
        type="button"
        aria-label={copied ? "Copied" : label}
        onClick={handleCopy}
        className={cn(
          "group inline-flex h-8 shrink-0 items-center justify-center rounded-md px-2",
          "text-sm font-medium ring-offset-background transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          variant === "outline"
            ? "border border-input bg-background hover:bg-accent hover:text-accent-foreground"
            : "hover:bg-accent hover:text-accent-foreground",
          className
        )}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-success" aria-hidden="true" />
        ) : (
          <Copy className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}
        <span
          className={cn(
            "ml-0 max-w-0 overflow-hidden whitespace-nowrap opacity-0 text-xs",
            "transition-all duration-200",
            "group-hover:ml-1.5 group-hover:max-w-[80px] group-hover:opacity-100"
          )}
        >
          {copied ? "Copied!" : label}
        </span>
      </button>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      aria-label={label}
      className={cn("gap-1.5", className)}
      {...rest}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {showLabel && (
        <span className="text-xs">{copied ? "Copied" : label}</span>
      )}
    </Button>
  );
}
